import { ErrataPolicy, ReadWaiveAnswer, ScriptAttendee } from '@/types'

/**
 * Who the errata sheet goes to, for screens that state it without offering the
 * picker. Mirrors Back Office's own summary of the same field.
 */
export const describeErrataRecipient = (
  attendees: ScriptAttendee[],
  attendeeId: string | null,
): string => {
  const recipient = attendees.find(
    attendee => attendee.attendee_id === attendeeId,
  )

  if (!recipient) {
    return 'Not recorded'
  }

  return `${recipient.name} — ${recipient.email || 'no email recorded'}`
}

/**
 * The answer the jurisdiction's rule supplies where the reporter is not asked,
 * so a script can state it instead of showing an empty field.
 */
export const fixedReadWaiveAnswer = (
  errataPolicy: ErrataPolicy | null | undefined,
): ReadWaiveAnswer | null => {
  if (errataPolicy === 'always_read') {
    return 'read'
  }

  return errataPolicy === 'always_waive' ? 'waive' : null
}

/**
 * Whether the reporter is asked read or waive at all. Where the jurisdiction's
 * rule is fixed either way there is nothing for the witness to decide, so both
 * scripts hide the question and the answer stays "no answer".
 */
export const isReadWaiveAsked = (
  errataPolicy: ErrataPolicy | null | undefined,
): boolean => errataPolicy !== 'always_read' && errataPolicy !== 'always_waive'

/**
 * The answer to keep on record. Where the question is not asked there is no
 * answer to keep: an event whose rule arrived mid-script can still hold one,
 * and reporting it as the witness' election would be wrong.
 */
export const readWaiveAnswerToRecord = (
  { isAsked, isEUO }: { isAsked: boolean; isEUO: boolean },
  current: ReadWaiveAnswer,
): ReadWaiveAnswer => (isAsked && !isEUO ? current : 'no_answer')

/**
 * Whether the reporter should record who receives the errata sheet: only where
 * the witness elected to read.
 *
 * The script follows the answer it was given and nothing else. Where nobody was
 * asked the answer is "no answer", and whether a sheet is nonetheless due is
 * parrot's and Back Office's business, not the script's.
 */
export const doesErrataApply = (readWaiveAnswer: ReadWaiveAnswer): boolean =>
  readWaiveAnswer === 'read'
